import { useCallback, useState } from 'react';

import { EntityRefUtil, GearbeltEvent, GetCheckResponseDTO, GetWorkOrdersResponseDTO, IssueDTO } from '@gearbelt/global';

import { FeedbackPayload, FeedbackPromptProps } from '@/components/feedback/feedback-prompt';
import { useUserStore } from '@/stores/user.store';
import { useEvents } from '@/utils/events';

const FEEDBACK_FREQUENCY = 5;
export const ENABLED_SURFACES = new Set<FeedbackPromptSurface>(['issues', 'schedule-checks']);

export type ClosureFeedbackSurface = 'work-orders' | 'issues' | 'schedule-checks';
export type RouteStateFeedbackSurface = 'deployment-rules' | 'schedule-editor';
export type FeedbackPromptSurface = ClosureFeedbackSurface | RouteStateFeedbackSurface;

type WorkOrder = GetWorkOrdersResponseDTO['data'][number];

interface BaseFeedbackPromptReturn {
  trigger: (entityContext?: Record<string, unknown>) => void;
  promptProps: FeedbackPromptProps;
}

interface ClosureFeedbackPromptReturn extends BaseFeedbackPromptReturn {
  triggerForWorkOrder: (workOrder: WorkOrder) => void;
  triggerForIssue: (issue: IssueDTO) => void;
  triggerForScheduleCheck: (check: GetCheckResponseDTO) => void;
}

interface SurfaceConfig {
  event: GearbeltEvent;
  heading: string;
  question: string;
  tags: string[];
}

const SURFACE_CONFIG: Record<FeedbackPromptSurface, SurfaceConfig> = {
  'work-orders': {
    event: GearbeltEvent.WorkOrderClosedFeedbackSubmitted,
    heading: 'Rate your experience closing this work order',
    question: "We're actively improving work orders, how easy was the close-out?",
    tags: [
      'Auto-creation and auto-assignment of work orders',
      'Too much manual work to close linked issues and schedule checks',
      'Costing on a work order level',
      'Other',
    ],
  },
  issues: {
    event: GearbeltEvent.IssueClosedFeedbackSubmitted,
    heading: 'Rate your experience closing this issue',
    question: "We're actively improving issues, how easy was the close-out?",
    tags: ['Editing repair forms after submission', 'Costing of an issue', "Reopening issues that weren't fixed correctly", 'Other'],
  },
  'schedule-checks': {
    event: GearbeltEvent.ScheduleCheckClosedFeedbackSubmitted,
    heading: 'Rate your experience closing this schedule check',
    question: "We're actively improving schedule checks, how easy was the close-out?",
    tags: ['Confused, not sure when the next check becomes due', 'Bulk-closing schedule checks', 'Other'],
  },
  'deployment-rules': {
    event: GearbeltEvent.DeploymentRuleFeedbackSubmitted,
    heading: 'Rate your experience setting up this rule?',
    question: "We're actively improving deployment rules, how easy was this to set up?",
    tags: [
      'Unclear how to set up a rule for my requirements',
      'Unclear which assets this deployment rule applies to',
      'Need to switch to asset list to view deployment',
      "Couldn't set up the conditions I needed",
      'Taking a long time to load',
      'Other',
    ],
  },
  'schedule-editor': {
    event: GearbeltEvent.ScheduleEditorFeedbackSubmitted,
    heading: 'Rate your experience setting up this schedule?',
    question: "We're actively improving the schedule editor, how easy was this to set up?",
    tags: ['Unclear how to set up a schedule for my requirements', 'Schedule preview was confusing', 'Taking a long time to load', 'Other'],
  },
};

export function useFeedbackPrompt(surface: ClosureFeedbackSurface): ClosureFeedbackPromptReturn;
export function useFeedbackPrompt(surface: RouteStateFeedbackSurface): BaseFeedbackPromptReturn;
export function useFeedbackPrompt(surface: FeedbackPromptSurface): ClosureFeedbackPromptReturn {
  const config = SURFACE_CONFIG[surface];
  const { user } = useUserStore();
  const { trackEvent } = useEvents();
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<Record<string, unknown>>({});

  const trigger = useCallback(
    (entityContext: Record<string, unknown> = {}) => {
      if (!user?.id) return;
      if (!ENABLED_SURFACES.has(surface)) return;
      const countKey = `gearbelt:feedback:${surface}:closeCount:${user.id}`;
      const newCount = Number(localStorage.getItem(countKey) ?? 0) + 1;
      localStorage.setItem(countKey, String(newCount));
      if (newCount % FEEDBACK_FREQUENCY !== 0) return;
      setContext(entityContext);
      setOpen(true);
    },
    [user?.id, surface],
  );

  const triggerForWorkOrder = useCallback(
    (workOrder: WorkOrder) => {
      trigger({
        workOrderId: workOrder.id,
        workOrderNumber: EntityRefUtil.createWorkOrderRef(workOrder.number),
        workOrderInstructions: workOrder.instructions ?? undefined,
        assetId: workOrder.asset?.id,
        assetName: workOrder.asset ? EntityRefUtil.createAssetLabel(workOrder.asset) : undefined,
      });
    },
    [trigger],
  );

  const triggerForIssue = useCallback(
    (issue: IssueDTO) => {
      trigger({
        issueId: issue.id,
        issueNumber: EntityRefUtil.createIssueRef(issue.number),
        issueTitle: issue.title ?? undefined,
        assetId: issue.asset?.id,
        assetName: issue.asset ? EntityRefUtil.createAssetLabel(issue.asset) : undefined,
      });
    },
    [trigger],
  );

  const triggerForScheduleCheck = useCallback(
    (check: GetCheckResponseDTO) => {
      trigger({
        scheduleCheckId: check.id,
        scheduleCheckNumber: EntityRefUtil.createCheckRef(check.number),
        checkTitle: check.schedule?.name ?? undefined,
        assetId: check.asset?.id,
        assetName: check.asset ? EntityRefUtil.createAssetLabel(check.asset) : undefined,
      });
    },
    [trigger],
  );

  const handleSubmit = useCallback(
    (payload: FeedbackPayload) => {
      trackEvent(config.event, { ...payload, ...context });
    },
    [trackEvent, config.event, context],
  );

  return {
    trigger,
    triggerForWorkOrder,
    triggerForIssue,
    triggerForScheduleCheck,
    promptProps: {
      open,
      onOpenChange: setOpen,
      surface,
      heading: config.heading,
      question: config.question,
      tags: config.tags,
      // Non-dismissible for work-orders specifically — required feedback, independent of ENABLED_SURFACES.
      dismissible: surface !== 'work-orders',
      onSubmit: handleSubmit,
    },
  };
}
